import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { toast } from "sonner";
import { Receipt, Upload, Loader2, AlertTriangle, RotateCw, TriangleAlert } from "lucide-react";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

export function DisputeChargeModule({ propertyId, onAudit }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [phaseLabel, setPhaseLabel] = useState("Reading receipt…");
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/deduction-audits/${propertyId}`);
      const latest = (data || [])[0] || null;
      setAudit(latest);
      if (latest && latest.status === "completed") onAudit?.(latest);
    } catch (e) { console.error("deduction audit load failed", e); /* non-blocking */ } finally { setLoading(false); }
  }, [propertyId, onAudit]);

  useEffect(() => { load(); }, [load]);

  // Poll every 3s while the latest audit is in flight.
  useEffect(() => {
    const active = audit && (audit.status === "pending" || audit.status === "processing");
    if (!active) { if (pollRef.current) clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(load, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [audit, load]);

  // Swap the processing sub-label after a few seconds (single 'processing' status covers both AI stages).
  useEffect(() => {
    if (audit?.status !== "processing") { setPhaseLabel("Reading receipt…"); return; }
    const t = setTimeout(() => setPhaseLabel("Estimating typical costs…"), 5000);
    return () => clearTimeout(t);
  }, [audit?.status, audit?.audit_id]);

  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("property_id", propertyId);
      form.append("file", file);
      const { data } = await api.post(`/deduction-audits`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setAudit({ audit_id: data.audit_id, status: "pending", line_items: [] });
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const retry = async () => {
    try {
      await api.post(`/deduction-audits/${audit.audit_id}/retry-processing`);
      setAudit((a) => ({ ...a, status: "pending", last_error: null }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Retry failed"); }
  };

  const status = audit?.status;
  const flagged = (audit?.line_items || []).filter((li) => li.flagged).length;
  const total = (audit?.line_items || []).length;

  return (
    <div data-testid="dispute-charge-module" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-amber-soft text-amber-ink"><Receipt size={18} /></span>
        <div>
          <h3 className="font-heading text-lg font-bold text-navy">Dispute a charge</h3>
          <p className="text-xs text-[#64748B]">Review your landlord's itemized deductions</p>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 h-20 rounded-xl bg-[#F1F5F9] animate-pulse" />
      ) : !audit || status === "failed" ? (
        <div className="mt-4">
          {status === "failed" ? (
            <div data-testid="audit-failed" className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#92400E]"><AlertTriangle size={16} /> Couldn't read this receipt</p>
              <p className="mt-1 text-xs text-[#92400E]">{audit.last_error || "Try a clearer, well-lit photo of the full itemized list."}</p>
              <Button data-testid="audit-retry-btn" onClick={retry} size="sm" className="mt-3 rounded-lg bg-navy hover:bg-navy-hover text-white"><RotateCw size={14} className="mr-1.5" /> Tap to retry</Button>
            </div>
          ) : (
            <p className="text-sm text-[#475569]">Received an itemized deduction list? Upload it and we'll review the charges.</p>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="audit-file-input" />
          <Button data-testid="audit-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="mt-3 rounded-xl h-11 bg-brand hover:bg-brand-hover text-white">
            {uploading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Uploading…</> : <><Upload size={16} className="mr-2" /> Upload receipt</>}
          </Button>
        </div>
      ) : status === "pending" || status === "processing" ? (
        <div data-testid="audit-processing" className="mt-4">
          <p className="flex items-center gap-2 text-sm font-medium text-[#475569]"><Loader2 size={16} className="animate-spin text-brand" /> {phaseLabel}</p>
          <div className="mt-3 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-9 rounded-lg bg-[#F1F5F9] animate-pulse" />)}</div>
        </div>
      ) : (
        <div data-testid="audit-results" className="mt-4">
          <div data-testid="audit-summary" className="mb-3 rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] px-4 py-2.5 text-sm font-semibold text-navy">
            {flagged} of {total} charges appear above typical ranges.
          </div>
          <div className="space-y-2">
            {(audit.line_items || []).map((li, i) => (
              <div key={i} data-testid={`audit-item-${i}`} className="rounded-xl border border-[#E2E8F0] px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-navy text-sm truncate">{li.description}</p>
                    {li.estimated_typical_low == null || li.estimated_typical_high == null ? (
                      <p className="text-xs text-[#94A3B8] italic mt-0.5">Couldn't estimate — too vague</p>
                    ) : (
                      <p className="text-xs text-[#64748B] mt-0.5">Typical: {money(li.estimated_typical_low)}–{money(li.estimated_typical_high)}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-navy">{money(li.claimed_cost)}</p>
                    {li.flagged && (
                      <span data-testid={`audit-flag-${i}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-soft text-amber-ink border border-amber-border text-[11px] font-semibold px-2 py-0.5">
                        <TriangleAlert size={11} /> Above typical range
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <LegalDisclaimer keyName="deduction_audit" className="mt-4 block text-[11px] leading-relaxed text-[#94A3B8]" />
        </div>
      )}
    </div>
  );
}
